import React, { useEffect, useState } from 'react';
import { api, adminHeaders } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CATS = [
    { value: 'equine', label: 'Equine' },
    { value: 'dog', label: 'Dog' },
    { value: 'other', label: 'Other' },
];

function Editor({ item, onSaved, onClose }) {
    const [data, setData] = useState({
        title: item?.title || '',
        category: item?.category || 'equine',
        medium: item?.medium || '',
        size: item?.size || '',
        description: item?.description || '',
        image_url: item?.image_url || '',
        sort_order: item?.sort_order ?? 0,
        featured: !!item?.featured,
    });
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            if (item && item.id) {
                await api.put(`/admin/gallery/${item.id}`, data, { headers: adminHeaders() });
            } else {
                const fd = new FormData();
                Object.entries(data).forEach(([k, v]) => {
                    if (v !== null && v !== undefined && v !== '') fd.append(k, String(v));
                });
                if (file) fd.append('file', file);
                await api.post('/admin/gallery', fd, { headers: { ...adminHeaders(), 'Content-Type': 'multipart/form-data' } });
            }
            toast.success('Saved.');
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label>Title</Label>
                    <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="gallery-edit-title" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div>
                    <Label>Category</Label>
                    <Select value={data.category} onValueChange={(v) => setData({ ...data, category: v })}>
                        <SelectTrigger data-testid="gallery-edit-category" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label>Medium</Label>
                    <Input value={data.medium} onChange={(e) => setData({ ...data, medium: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div>
                    <Label>Size</Label>
                    <Input value={data.size} onChange={(e) => setData({ ...data, size: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div className="sm:col-span-2">
                    <Label>Description</Label>
                    <Textarea value={data.description} onChange={(e) => setData({ ...data, description: e.target.value })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                {!item && (
                    <>
                        <div>
                            <Label>Image URL (optional)</Label>
                            <Input value={data.image_url} onChange={(e) => setData({ ...data, image_url: e.target.value })} placeholder="https://…" data-testid="gallery-edit-image-url" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                        </div>
                        <div>
                            <Label>Or upload file</Label>
                            <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gallery-edit-file" className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                        </div>
                    </>
                )}
                <div>
                    <Label>Sort order</Label>
                    <Input type="number" value={data.sort_order} onChange={(e) => setData({ ...data, sort_order: Number(e.target.value) })} className="mt-1 bg-[color:var(--paper)] border-[color:var(--border)]" />
                </div>
                <div className="flex items-center gap-2">
                    <Switch checked={data.featured} onCheckedChange={(v) => setData({ ...data, featured: v })} data-testid="gallery-edit-featured" />
                    <Label>Featured</Label>
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-3">
                <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="gallery-edit-save-button" className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]">{saving ? 'Saving…' : 'Save'}</Button>
            </div>
        </div>
    );
}

export default function AdminGallery() {
    const [items, setItems] = useState([]);
    const [openAdd, setOpenAdd] = useState(false);
    const [editing, setEditing] = useState(null);

    async function load() {
        const r = await api.get('/admin/gallery', { headers: adminHeaders() });
        setItems(r.data);
    }
    useEffect(() => { load(); }, []);

    async function del(id) {
        if (!window.confirm('Delete this gallery item?')) return;
        await api.delete(`/admin/gallery/${id}`, { headers: adminHeaders() });
        toast.success('Deleted.');
        load();
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <p className="caption">Gallery</p>
                    <h1 className="font-display text-3xl text-[color:var(--umber)] mt-1">Portfolio images</h1>
                </div>
                <Dialog open={openAdd} onOpenChange={setOpenAdd}>
                    <DialogTrigger asChild>
                        <Button data-testid="gallery-add-button" className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]"><Plus className="h-4 w-4 mr-1" /> Add image</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl bg-[color:var(--paper)] border-[color:var(--border)]">
                        <DialogTitle>Add gallery image</DialogTitle>
                        <Editor item={null} onSaved={load} onClose={() => setOpenAdd(false)} />
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-gallery-grid">
                {items.map((g) => (
                    <div key={g.id} className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] overflow-hidden">
                        <div className="aspect-square overflow-hidden bg-[color:var(--paper)]">
                            <img src={g.image_b64 || g.image_url} alt={g.title || ''} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-display text-base text-[color:var(--umber)]">{g.title || 'Untitled'}</p>
                                    <p className="text-xs text-[color:var(--muted-fg)] capitalize">{g.category}{g.featured ? ' · featured' : ''}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button size="sm" variant="outline" onClick={() => setEditing(g)} data-testid={`gallery-edit-${g.id}`} className="rounded-xl bg-[color:var(--paper)] border-[color:var(--border)]"><Pencil className="h-3 w-3" /></Button>
                                        </DialogTrigger>
                                        <DialogContent className="max-w-2xl bg-[color:var(--paper)] border-[color:var(--border)]">
                                            <DialogTitle>Edit {g.title || 'image'}</DialogTitle>
                                            <Editor item={editing && editing.id === g.id ? editing : g} onSaved={load} onClose={() => setEditing(null)} />
                                        </DialogContent>
                                    </Dialog>
                                    <Button size="sm" variant="outline" onClick={() => del(g.id)} data-testid={`gallery-delete-${g.id}`} className="rounded-xl bg-[color:var(--paper)] border-[color:var(--border)]"><Trash2 className="h-3 w-3" /></Button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="col-span-full text-center py-16 text-[color:var(--muted-fg)]" data-testid="empty-state">No gallery items yet.</div>
                )}
            </div>
        </div>
    );
}
